import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auction as auctionApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, Clock, CheckCircle2, AlertTriangle, ShieldCheck, ArrowRight, Timer, Lock } from 'lucide-react';

/**
 * Winner cash-checkout screen. Shows the total the winner owes, the 48h
 * countdown, and a "Pay with Card" button that redirects to Stripe Checkout.
 * On return, /auction/:aid/checkout/success verifies the payment.
 */
export default function AuctionCheckout() {
  const { aid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [remaining, setRemaining] = useState(null); // ms

  useEffect(() => {
    if (!user) { navigate(`/login?redirect=/auction/${aid}/checkout`); return; }
    auctionApi.checkoutState(aid)
      .then(setState)
      .catch(e => setErr(e?.response?.data?.detail || 'Could not load checkout'))
      .finally(() => setLoading(false));
  }, [aid, user, navigate]);

  useEffect(() => {
    if (!state?.checkoutDeadline) return;
    const tick = () => {
      const ms = new Date(state.checkoutDeadline).getTime() - Date.now();
      setRemaining(ms);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [state?.checkoutDeadline]);

  const remainingLabel = useMemo(() => {
    if (remaining == null) return '';
    if (remaining <= 0) return 'Expired';
    const s = Math.floor(remaining / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return `${h}h ${String(m).padStart(2, '0')}m ${String(r).padStart(2, '0')}s`;
  }, [remaining]);

  const payNow = async () => {
    setStarting(true);
    try {
      const res = await auctionApi.checkoutSession(aid);
      // Redirect to Stripe Checkout
      window.location.href = res.url;
    } catch (e) {
      toast({ title: 'Checkout failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-green-400" size={24} />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="pbb-card p-8 text-center max-w-md w-full" data-testid="checkout-error">
          <AlertTriangle className="mx-auto text-red-400" size={30} />
          <div className="mt-3 font-semibold">We couldn't load your checkout</div>
          <p className="text-sm text-gray-400 mt-1">{err}</p>
          <Link to="/auction" className="pbb-btn-green mt-5 inline-block max-w-xs">Back to Auction Store</Link>
        </div>
      </div>
    );
  }

  const paid = state?.checkoutStatus === 'paid';
  const expired = remaining !== null && remaining <= 0 && !paid;
  const cascadeAttempt = state?.cascadeAttempt || 1;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="auction-checkout">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-green-400">C.A.N. Auction · Winner Checkout</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">{state?.title || 'Your winning bid'}</h1>
        {cascadeAttempt > 1 && (
          <div className="mt-2 inline-flex items-center gap-1 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
            Cascade round #{cascadeAttempt} — previous winner didn't pay in time
          </div>
        )}
      </div>

      {state?.image && (
        <div className="pbb-card p-0 overflow-hidden mb-6">
          <img src={state.image} alt={state.title} className="w-full max-h-56 object-cover" />
        </div>
      )}

      <div className="pbb-card p-5 space-y-3" data-testid="checkout-breakdown">
        <Row label="Item set price" value={state?.setPrice} />
        {state?.shippingCost > 0 && <Row label="Shipping" value={state.shippingCost} />}
        {state?.taxes > 0 && <Row label="Estimated tax" value={state.taxes} />}
        {state?.handling > 0 && <Row label="Handling" value={state.handling} />}
        <div className="pt-3 border-t border-[#1f1f1f] flex items-center justify-between font-bold">
          <span>Total due</span>
          <span className="text-green-400 text-lg tabular-nums" data-testid="checkout-total">${(state?.checkoutTotal || 0).toFixed(2)}</span>
        </div>
      </div>

      {paid ? (
        <div className="pbb-card p-6 mt-5 text-center bg-green-500/5 border-green-500/40" data-testid="checkout-paid">
          <CheckCircle2 className="mx-auto text-green-400" size={30} />
          <div className="mt-3 font-semibold">Payment complete</div>
          <p className="text-sm text-gray-400 mt-1">
            Thank you! ${(state.checkoutPaidAmountUsd || 0).toFixed(2)} routed to the {state.checkoutAllocation === 'capital' ? 'Capital Fund' : 'Community + Youth funds'}.
          </p>
          <Link to="/auction" className="pbb-btn-green inline-flex items-center gap-1 mt-4 max-w-xs mx-auto">
            Back to auctions <ArrowRight size={14} />
          </Link>
        </div>
      ) : expired ? (
        <div className="pbb-card p-6 mt-5 text-center bg-red-500/5 border-red-500/40" data-testid="checkout-expired">
          <Timer className="mx-auto text-red-400" size={30} />
          <div className="mt-3 font-semibold">Your 48-hour window expired</div>
          <p className="text-sm text-gray-400 mt-1">This win has cascaded to the next bidder. You'll see a notification if it comes back around.</p>
          <Link to="/auction" className="pbb-btn-green mt-5 inline-block max-w-xs">Back to auctions</Link>
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-2 text-sm text-gray-300" data-testid="checkout-countdown">
            <Clock size={16} className="text-amber-400" />
            <span>You have <b className="text-amber-300 tabular-nums">{remainingLabel}</b> to complete checkout.</span>
          </div>
          <button
            type="button"
            onClick={payNow}
            disabled={starting}
            data-testid="checkout-pay-btn"
            className="mt-4 w-full pbb-btn-green inline-flex items-center justify-center gap-2 py-3 text-base disabled:opacity-60"
          >
            {starting ? <Loader2 className="animate-spin" size={16} /> : <Lock size={16} />} Pay with Card · ${(state?.checkoutTotal || 0).toFixed(2)}
          </button>
          <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-gray-500">
            <ShieldCheck size={12} className="text-green-400" /> Secure checkout by Stripe · 100% of your payment funds the {new Date() < new Date('2027-04-01') ? 'Capital Fund' : 'Community + Youth funds'}.
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-400">{label}</span>
      <span className="tabular-nums">${(value || 0).toFixed(2)}</span>
    </div>
  );
}
